import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Python 3.11', icon: Server },
  { name: 'XGBoost / LightGBM', icon: Database },
  { name: 'TensorFlow / scikit-learn', icon: Cloud },
  { name: 'PostgreSQL (Azure)', icon: Database },
  { name: 'GitHub Actions', icon: Shield },
  { name: 'Power BI', icon: FileText }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '65%',
    label: 'Prediction Accuracy',
    description: 'Across Soccer, NBA, and NASCAR from a single production platform'
  },
  {
    icon: Clock,
    value: '1 Month',
    label: 'To Production',
    description: 'From concept to fully automated production deployment'
  },
  {
    icon: Database,
    value: '70K+',
    label: 'Training Matches',
    description: 'In soccer dataset across 10 leagues and 10 years'
  },
  {
    icon: CheckCircle2,
    value: 'Positive',
    label: 'ROI from Month 1',
    description: 'Delivered from first month of commercial prediction operations'
  }
];

const highlights = [
  {
    category: 'Prediction Performance',
    items: [
      'Soccer V2 Grade A Over/Under predictions achieved 89.2% win rate with +0.28 units average profit per bet',
      'NBA moneyline Grade A predictions achieved 72.3% win rate with +18.5% ROI through XGBoost home/away models',
      'NASCAR Top 10 finishing position accuracy reached 58.7% across 46 evaluated races with track-specific model selection'
    ]
  },
  {
    category: 'Platform Automation',
    items: [
      'Deployed fully automated Fetch → Predict → Store → Validate daily pipeline across all three sports via GitHub Actions',
      'Custom ROI-based grading system assigns A through D grades based on sport-specific confidence thresholds',
      'Power BI dashboards provide real-time tracking of prediction outcomes, grade distribution, and profit/loss by sport'
    ]
  }
];

const challenges = [
  {
    title: 'Three Sports, Three Completely Different Model Requirements',
    icon: Database,
    problem: 'Soccer, NBA, and NASCAR each require different data sources, features, model types, and validation logic — a single model approach would not work across all three, but separate systems would be impossible to maintain.',
    solution: 'Built modular sport-specific model pipelines under a shared orchestration and storage layer, enabling independent feature engineering and model architecture per sport while sharing common infrastructure and automation.',
    impact: 'Three production models operating daily from a single automated platform'
  },
  {
    title: 'Reliable API Integration Across Multiple Providers',
    icon: Cloud,
    problem: 'Sports data APIs are inconsistent — endpoints change, data is delayed, match statuses vary, and a single API failure would break the entire daily pipeline with no recovery mechanism.',
    solution: 'Implemented multi-API fallback logic across three API configurations tested in order for match status and scores, with retry handling and validation gates before storing any prediction to the database.',
    impact: 'Reliable daily pipeline execution across all three sports with no manual intervention'
  },
  {
    title: 'Limited Pre-Match Data Window',
    icon: Server,
    problem: 'All predictions must be generated 2–3 days before matches with no access to live data, requiring models that perform well purely on historical and pre-match statistical features.',
    solution: 'Engineered pre-match features capturing rolling form, head-to-head history, Elo ratings, points per game differentials, and market-implied probabilities — features predictive without requiring live inputs.',
    impact: 'Positive ROI from first month of production operation across all three sports'
  },
  {
    title: 'Azure Firewall Blocking Automated Pipeline',
    icon: Shield,
    problem: 'GitHub Actions CI/CD pipeline was blocked by Azure PostgreSQL firewall rules, preventing automated prediction storage and breaking the daily workflow entirely.',
    solution: 'Whitelisted GitHub Actions IP ranges in Azure firewall configuration, enabling the fully automated pipeline to store predictions to the database without any manual steps.',
    impact: 'Fully automated daily pipeline with zero manual database access required'
  }
];

const SportsPrediction = () => {
  return (
    <>
      <SEOHead
        title="AI-Powered Sports Prediction Platform Case Study | Agility AI"
        description="How Agility built a production ML prediction platform across Soccer, NBA, and NASCAR — 65% average accuracy, positive ROI from month one, 70K+ training matches, fully automated daily pipelines."
        url="https://www.agilitytech.ai/case-studies/sports-prediction"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "AI-Powered Sports Prediction Platform",
          "description": "How Agility built a production ML prediction platform across Soccer, NBA, and NASCAR — 65% average accuracy, positive ROI from month one, 70K+ training matches, fully automated daily pipelines.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2024-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/sports-prediction" }
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80"
              alt="Sports Prediction Platform Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Sports & Entertainment</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                AI-Powered Sports Prediction Platform
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Building a production-grade multi-sport machine learning prediction system across Soccer, NBA, and NASCAR with daily automated pipelines, custom grading, and positive ROI from month one.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">Sports & Entertainment</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">Global</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">Multi-Sport, Multi-League Production Platform</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">Q3 2024 - Completed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Multi-Sport Prediction Across Three Disciplines</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      Building reliable ML predictions simultaneously across Soccer, NBA, and NASCAR presented a fundamental challenge: each sport required entirely different data sources, feature engineering approaches, model architectures, and validation logic — all operating on a 2–3 day pre-match data window with no live data available at prediction time. Integrating multiple sports APIs reliably, handling model complexity without overfitting, and automating daily execution across three pipelines required a production-grade system built from the ground up.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Production Multi-Sport ML Platform</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Production Multi-Sport ML Platform</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We built and deployed a fully automated, production-grade prediction platform covering Soccer, NBA, and NASCAR from a single shared infrastructure. Soccer V2 Grade A Over/Under predictions reached 89.2% win rate; NBA moneyline Grade A hit 72.3% win rate with +18.5% ROI; NASCAR Top 10 accuracy reached 58.7% across 46 races. From concept to live automated operation took one month, with positive ROI delivered from the first commercial month across all three sports.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8
                  border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                        Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                        Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>

                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Build AI-Powered Prediction Systems?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our machine learning team to discover how production-grade ML pipelines can deliver measurable ROI from automated prediction platforms.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SportsPrediction;
